import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { setSelectedWeight, setSelectedJacketSize, setSelectedPatches, setCustomPhoto, setBodyMeasurements, setSelectedFit } from '../store/appSlice';
import { storage } from '../firebase';
import { ref, uploadString, getDownloadURL } from 'firebase/storage';
import './ProductDetailScreen.css';

/** Decode query values that may be URL-encoded or double-encoded (Shopify liquid url_param_escape). */
function decodeUrlParam(value) {
  if (value == null || value === '') return value;
  let s = String(value).replace(/\+/g, ' ');
  for (let i = 0; i < 5; i += 1) {
    try {
      const next = decodeURIComponent(s);
      if (next === s) break;
      s = next;
    } catch {
      break;
    }
  }
  return s;
}

function isMyshopifyHost(hostname) {
  return typeof hostname === 'string' && /\.myshopify\.com$/i.test(hostname);
}

/** Primary storefront origin for /cart/add — must match where customers open /cart (custom domain vs myshopify are different cart sessions). */
function getStorefrontOrigin(urlParams) {
  const sf = urlParams.storefront;
  if (sf && String(sf).trim()) {
    try {
      const s = String(sf).trim();
      const urlStr = s.startsWith('http') ? s : `https://${s}`;
      let origin = new URL(urlStr).origin.replace(/\/$/, '');
      if (isMyshopifyHost(new URL(origin).hostname)) {
        const envCanonical = process.env.REACT_APP_SHOPIFY_STOREFRONT_ORIGIN;
        if (envCanonical && String(envCanonical).trim()) {
          const e = String(envCanonical).trim();
          origin = new URL(e.startsWith('http') ? e : `https://${e}`).origin.replace(/\/$/, '');
        }
      }
      return origin;
    } catch {
      /* fall through */
    }
  }
  const envFallback = process.env.REACT_APP_SHOPIFY_STOREFRONT_ORIGIN;
  if (envFallback && String(envFallback).trim()) {
    try {
      const e = String(envFallback).trim();
      return new URL(e.startsWith('http') ? e : `https://${e}`).origin.replace(/\/$/, '');
    } catch {
      /* fall through */
    }
  }
  if (urlParams.shop) {
    return `https://${urlParams.shop}`.replace(/\/$/, '');
  }
  return '';
}

// Size Chart Data for Male - Men's Size Chart (Chest, Waist, Length, Sleeves, Shoulder)
const MALE_SIZE_CHART = {
  'XS': {
    chestBody: '40',
    jacketChest: '40',
    waist: '31-32',
    shoulder: '17.5',
    sleeve: '25',
    armWidth: '12.5',
    length: '26'
  },
  'S': {
    chestBody: '42',
    jacketChest: '42',
    waist: '33-34',
    shoulder: '18',
    sleeve: '25.5',
    armWidth: '13.5',
    length: '26.5'
  },
  'M': {
    chestBody: '44',
    jacketChest: '44',
    waist: '35-36',
    shoulder: '18.5',
    sleeve: '26',
    armWidth: '14',
    length: '27'
  },
  'L': {
    chestBody: '46',
    jacketChest: '46',
    waist: '37-38',
    shoulder: '19',
    sleeve: '26.5',
    armWidth: '14.5',
    length: '27.5'
  },
  'XL': {
    chestBody: '48',
    jacketChest: '48',
    waist: '39-40',
    shoulder: '19.5',
    sleeve: '27',
    armWidth: '15',
    length: '28'
  },
  '2XL': {
    chestBody: '50',
    jacketChest: '50',
    waist: '41-42',
    shoulder: '20',
    sleeve: '27.5',
    armWidth: '15.5',
    length: '28.5'
  },
  '3XL': {
    chestBody: '52',
    jacketChest: '52',
    waist: '43-44',
    shoulder: '21',
    sleeve: '28',
    armWidth: '16',
    length: '29'
  },
  '4XL': {
    chestBody: '54',
    jacketChest: '54',
    waist: '45-46',
    shoulder: '22',
    sleeve: '28.5',
    armWidth: '16.5',
    length: '29.5'
  },
  '5XL': {
    chestBody: '56',
    jacketChest: '56',
    waist: '47-48',
    shoulder: '23',
    sleeve: '29',
    armWidth: '17',
    length: '30'
  }
};

// Size Chart Data for Female - Women's Size Chart (Bust, Waist, Hips, Sleeves, Shoulder)
const FEMALE_SIZE_CHART = {
  'XS': {
    chestBody: '33',
    jacketChest: '38',
    waist: '27-28',
    shoulder: '14-14.5',
    sleeve: '22.5-23',
    armWidth: '35-36',
    length: '24'
  },
  'S': {
    chestBody: '35-36',
    jacketChest: '40',
    waist: '29-30',
    shoulder: '14.75-15',
    sleeve: '23.75-24.25',
    armWidth: '36-37',
    length: '24.5'
  },
  'M': {
    chestBody: '37-38',
    jacketChest: '42',
    waist: '31-32',
    shoulder: '15.5-16',
    sleeve: '24.5-25',
    armWidth: '38-39',
    length: '25'
  },
  'L': {
    chestBody: '39-40',
    jacketChest: '44',
    waist: '33-34',
    shoulder: '16-16.5',
    sleeve: '25-25.5',
    armWidth: '40-41',
    length: '25.5'
  },
  'XL': {
    chestBody: '41-42',
    jacketChest: '46',
    waist: '35-36',
    shoulder: '16.5-17',
    sleeve: '25.5-26',
    armWidth: '42-43',
    length: '26'
  },
  '2XL': {
    chestBody: '43-44',
    jacketChest: '48',
    waist: '37-38',
    shoulder: '17-17.5',
    sleeve: '26-26.5',
    armWidth: '44-45',
    length: '26.5'
  }
};

const getPatchDesignsForPlacement = (placement) => {
  switch (placement) {
    case 'ZIP':
      return [
        { type: 'none', label: 'None' },
        { image: '/images/patchimg.png', type: 'image' },
        { image: '/images/patchimg.png', type: 'image' },
        { image: '/images/patchimg.png', type: 'image' },
        { image: '/images/patchimg.png', type: 'image' },
        { image: '/images/patchimg.png', type: 'image' },
        { image: '/images/patchimg.png', type: 'image' }
      ];
    case 'Right Arm':
      return [
        { type: 'none', label: 'None' },
        { image: '/images/patchimg.png', type: 'image' },
        { image: '/images/patchimg.png', type: 'image' },
        { image: '/images/patchimg.png', type: 'image' },
        { image: '/images/patchimg.png', type: 'image' },
        { image: '/images/patchimg.png', type: 'image' },
        { image: '/images/patchimg.png', type: 'image' }
      ];
    case 'Left Arm':
      return [
        { image: '/images/patchimg.png', type: 'image' },
        { image: '/images/patchimg.png', type: 'image' },
        { image: '/images/patchimg.png', type: 'image' },
        { image: '/images/patchimg.png', type: 'image' },
        { image: '/images/patchimg.png', type: 'image' },
        { image: '/images/patchimg.png', type: 'image' }
      ];
    case 'Back Side':
      return [
        { type: 'custom', label: 'Upload Custom Image' },
        { type: 'text', label: 'Add Custom Text' },
        { image: '/images/patchimg.png', type: 'image' },
        { image: '/images/patchimg.png', type: 'image' },
        { image: '/images/patchimg.png', type: 'image' },
        { image: '/images/patchimg.png', type: 'image' },
        { image: '/images/patchimg.png', type: 'image' },
        { image: '/images/patchimg.png', type: 'image' }
      ];
    default:
      return [
        { image: '/images/patchimg.png', type: 'image' },
        { image: '/images/patchimg.png', type: 'image' },
        { image: '/images/patchimg.png', type: 'image' },
        { image: '/images/patchimg.png', type: 'image' },
        { image: '/images/patchimg.png', type: 'image' },
        { image: '/images/patchimg.png', type: 'image' }
      ];
  }
};

const ProductDetailScreen = () => {
  const dispatch = useDispatch();
  const { selectedWeight, selectedJacketSize, selectedFit, selectedPatches, customPhoto, bodyMeasurements } = useSelector(state => state.app);
  
  const [isJacketSizeModalVisible, setIsJacketSizeModalVisible] = useState(false);
  const [isBodySizeModalVisible, setIsBodySizeModalVisible] = useState(false);
  const [cameFromJacketSizeModal, setCameFromJacketSizeModal] = useState(false);
  const [tempBodyMeasurements, setTempBodyMeasurements] = useState(bodyMeasurements);
  const isFromSizeSelection = useRef(false);
  const [isPatchesModalVisible, setIsPatchesModalVisible] = useState(false);
  const [selectedPatch, setSelectedPatch] = useState(0);
  const [selectedPlacement, setSelectedPlacement] = useState('ZIP');
  const [isBottomSheetVisible, setIsBottomSheetVisible] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [customText, setCustomText] = useState('');
  const [textColor, setTextColor] = useState('#ffffff');
  const [textSize, setTextSize] = useState('24');
  const [customImageSize, setCustomImageSize] = useState(50); 
  const [currentImageView, setCurrentImageView] = useState('front'); 
  const [backSidePatchPosition, setBackSidePatchPosition] = useState({ x: 50, y: 45 }); 
  const [isDragging, setIsDragging] = useState(false);
  const [customizedBackImage, setCustomizedBackImage] = useState(null);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [customizedBackImageUrl, setCustomizedBackImageUrl] = useState(null); 
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [customPhotoUrl, setCustomPhotoUrl] = useState(null); 
  const [textPreviewImage, setTextPreviewImage] = useState(null); 
  const isDraggingRef = useRef(false);
  const currentImageViewRef = useRef(currentImageView);
  
  useEffect(() => {
    if (isBottomSheetVisible) {
      document.body.classList.add('bottom-sheet-open');
      document.documentElement.classList.add('bottom-sheet-open');
    } else {
      document.body.classList.remove('bottom-sheet-open');
      document.documentElement.classList.remove('bottom-sheet-open');
    }
    
    return () => {
      document.body.classList.remove('bottom-sheet-open');
      document.documentElement.classList.remove('bottom-sheet-open');
    };
  }, [isBottomSheetVisible]);
  
  const [urlParams, setUrlParams] = useState({
    variant_id: null,
    shop: null,
    storefront: null,
    product_id: null,
    productHandle: null,
    title: null,
    price: null,
    image: null,
    description: null,
    collection: null
  });

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const newParams = {
      variant_id: params.get('variant_id') || params.get('variant'),
      shop: params.get('shop'),
      storefront: decodeUrlParam(params.get('storefront')),
      product_id: params.get('product_id'),
      productHandle: decodeUrlParam(params.get('productHandle')),
      title: decodeUrlParam(params.get('title')),
      price: params.get('price'),
      image: decodeUrlParam(params.get('image')),
      description: decodeUrlParam(params.get('description')),
      collection: decodeUrlParam(params.get('collection')) // Optional - will be auto-detected if not provided
    };
    setUrlParams(newParams);
    
    // Always fetch product data from Shopify to get collection and other missing data
    if (newParams.productHandle && newParams.shop) {
      fetchProductFromShopify(newParams.shop, newParams.productHandle, newParams.variant_id, newParams.product_id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  
  // Function to extract size from Shopify variant title
  const extractSizeFromVariant = (variantTitle, variantOptions) => {
    if (!variantTitle) return null;
    
    // Helper function to normalize size
    const normalizeSize = (sizeStr) => {
      if (!sizeStr) return null;
      const trimmed = sizeStr.trim();
      // Map common size names to size codes
      const sizeMap = {
        'XS': 'XS', 'Extra Small': 'XS', 'extra small': 'XS', 'xs': 'XS',
        'S': 'S', 'Small': 'S', 'small': 'S',
        'M': 'M', 'Medium': 'M', 'medium': 'M',
        'L': 'L', 'Large': 'L', 'large': 'L',
        'XL': 'XL', 'Extra Large': 'XL', 'extra large': 'XL', 'xl': 'XL',
        '2XL': '2XL', 'XXL': '2XL', '2X': '2XL', '2xl': '2XL', 'xxl': '2XL',
        '3XL': '3XL', 'XXXL': '3XL', '3X': '3XL', '3xl': '3XL', 'xxxl': '3XL',
        '4XL': '4XL', 'XXXXL': '4XL', '4X': '4XL', '4xl': '4XL', 'xxxxl': '4XL',
        '5XL': '5XL', 'XXXXXL': '5XL', '5X': '5XL', '5xl': '5XL', 'xxxxxl': '5XL'
      };
      
      if (sizeMap[trimmed]) {
        return sizeMap[trimmed];
      }
      // If direct match doesn't work, check if it's already a valid size code
      const upperTrimmed = trimmed.toUpperCase();
      if (['XS', 'S', 'M', 'L', 'XL', '2XL', '3XL', '4XL', '5XL'].includes(upperTrimmed)) {
        return upperTrimmed;
      }
      return null;
    };
    
    // Try to get size from variant options first (option2 is usually size, option3 might be size if option2 is something else)
    if (variantOptions) {
      // Try option2 first (most common for size)
      if (variantOptions.option2) {
        const size = normalizeSize(variantOptions.option2);
        if (size) return size;
      }
      // Try option3 as fallback
      if (variantOptions.option3) {
        const size = normalizeSize(variantOptions.option3);
        if (size) return size;
      }
      // If only one option exists and it's not option1 (which is usually color), it might be size
      if (!variantOptions.option2 && variantOptions.option1) {
        // Check if option1 might be size (if it matches size pattern)
        const size = normalizeSize(variantOptions.option1);
        if (size) return size;
      }
    }
    
    // Fallback: Extract from variant title (e.g., "Black / S" or "Black | S" or "S")
    // eslint-disable-next-line no-useless-escape
    const titleParts = variantTitle.split(/[\/|]/).map(part => part.trim());
    
    // Look for size in title parts (usually the last part is size)
    // Check from end to start as size is typically last
    for (let i = titleParts.length - 1; i >= 0; i--) {
      const part = titleParts[i];
      const size = normalizeSize(part);
      if (size) return size;
    }
    
    // If no size found in parts, check the whole title
    const size = normalizeSize(variantTitle);
    if (size) return size;
    
    return null;
  };

  // Function to fetch product data from Shopify and detect collection
  const fetchProductFromShopify = async (shop, productHandle, variantId, productId) => {
    try {
      const productUrl = `https://${shop}/products/${productHandle}.js`;
      const response = await fetch(productUrl);
      
      if (response.ok) {
        const productData = await response.json();
        
        const variantMatch = variantId
          ? productData.variants.find(v => v.id.toString() === variantId.toString())
          : null;
        let selectedVariant = variantMatch || productData.variants[0];
        
        // Detect collection from multiple sources
        let collectionName = null;
        
        // Method 1: Check product type
        if (productData.type) {
          const typeLower = productData.type.toLowerCase();
          if (typeLower.includes('female') || typeLower.includes('women') || typeLower.includes('ladies')) {
            collectionName = 'female';
          } else if (typeLower.includes('male') || typeLower.includes('men') || typeLower.includes('mens')) {
            collectionName = 'male';
          }
        }
        
        // Method 2: Check product tags
        if (!collectionName && productData.tags && productData.tags.length > 0) {
          for (const tag of productData.tags) {
            const tagLower = tag.toLowerCase();
            if (tagLower.includes('female') || tagLower.includes('women') || tagLower.includes('ladies')) {
              collectionName = 'female';
              break;
            } else if (tagLower.includes('male') || tagLower.includes('men') || tagLower.includes('mens')) {
              collectionName = 'male';
              break;
            }
          }
        }
        
        // Method 3: Check product vendor
        if (!collectionName && productData.vendor) {
          const vendorLower = productData.vendor.toLowerCase();
          if (vendorLower.includes('female') || vendorLower.includes('women')) {
            collectionName = 'female';
          } else if (vendorLower.includes('male') || vendorLower.includes('men')) {
            collectionName = 'male';
          }
        }
        
        // Method 4: Try to fetch collections and check which one contains this product
        if (!collectionName) {
          try {
            // Fetch all collections
            const collectionsUrl = `https://${shop}/collections.json`;
            const collectionsResponse = await fetch(collectionsUrl);
            if (collectionsResponse.ok) {
              const collectionsData = await collectionsResponse.json();
              
              // Check each collection to see if it contains this product
              for (const collection of collectionsData.collections || []) {
                try {
                  // Fetch products in this collection
                  const collectionProductsUrl = `https://${shop}/collections/${collection.handle}/products.json`;
                  const collectionProductsResponse = await fetch(collectionProductsUrl);
                  
                  if (collectionProductsResponse.ok) {
                    const collectionProductsData = await collectionProductsResponse.json();
                    // Check if current product is in this collection
                    const productInCollection = collectionProductsData.products?.some(p => 
                      p.handle === productHandle || p.id.toString() === productId?.toString()
                    );
                    
                    if (productInCollection) {
                      const collectionLower = collection.title.toLowerCase();
                      // Check if this collection indicates gender
                      // Check for female/women collections
                      if (collectionLower.includes('female') || collectionLower.includes('women') || 
                          collectionLower.includes('ladies') || collectionLower.includes('womens') ||
                          collectionLower.includes("women's")) {
                        collectionName = 'female';
                        break;
                      } 
                      // Check for male/men collections
                      else if (collectionLower.includes('male') || collectionLower.includes('men') || 
                                 collectionLower.includes('mens') || collectionLower.includes("men's")) {
                        collectionName = 'male';
                        break;
                      }
                    }
                  }
                } catch (err) {
                  // Continue to next collection if this one fails
                  continue;
                }
              }
            }
          } catch (err) {
            // Collections fetch failed, continue with default
          }
        }
        
        // Default to male if no collection detected
        if (!collectionName) {
          collectionName = 'male'; // Default
        }
        
        // Get the final collection value (from URL params or detected)
        // Use existing URL param if available, otherwise use detected collection
        const finalCollection = urlParams.collection || collectionName;

        const shopifyImageFromObject = (img) => {
          if (!img) return null;
          if (typeof img === 'string') {
            if (img.startsWith('//')) return `https:${img}`;
            return img;
          }
          if (img.src) {
            return img.src.startsWith('//') ? `https:${img.src}` : img.src;
          }
          return null;
        };

        // Important: Shopify's /products/{handle}.js often has `featured_image: null` for every variant.
        // In that case, we should NOT overwrite the URL image (which came from Liquid and is correct per-variant)
        // with the product's default featured image (often the first color = black).
        const variantSpecificImage = () => {
          const fi = selectedVariant?.featured_image;
          return shopifyImageFromObject(fi);
        };

        const resolvedImage = variantMatch ? variantSpecificImage() : null;
        const resolvedPrice =
          variantMatch && variantMatch.price != null
            ? (variantMatch.price / 100).toFixed(2)
            : null;

        // Update URL params with fetched data — when variant_id matches, trust API image/price over URL (URL image is often first variant / stale from Liquid)
        setUrlParams(prev => ({
          ...prev,
          image: resolvedImage || prev.image || shopifyImageFromObject(productData.featured_image),
          price: resolvedPrice || prev.price || (productData.price ? (Number(productData.price) / 100).toFixed(2) : null),
          title: prev.title || productData.title,
          description: prev.description || productData.description,
          collection: prev.collection || collectionName // Use URL param if provided, otherwise use detected
        }));
        
        // Extract size from variant if user came from Shopify with variant_id
        if (variantId && selectedVariant) {
          // Determine the correct size chart based on final collection
          const isFemale = finalCollection && finalCollection.toLowerCase().includes('female');
          const currentSizeChart = isFemale ? FEMALE_SIZE_CHART : MALE_SIZE_CHART;
          const availableSizes = Object.keys(currentSizeChart);
          
          // Extract size from variant
          const extractedSize = extractSizeFromVariant(
            selectedVariant.title, 
            {
              option1: selectedVariant.option1,
              option2: selectedVariant.option2,
              option3: selectedVariant.option3
            }
          );
          
          // If size was extracted and it's a valid size for the collection, set it as default
          if (extractedSize && availableSizes.includes(extractedSize)) {
            dispatch(setSelectedJacketSize(extractedSize));
            // Pre-populate measurements based on the selected size
            if (currentSizeChart[extractedSize]) {
              const sizeData = currentSizeChart[extractedSize];
              const getVal = (v) => (v && String(v).includes('-') ? String(v).split('-')[0].trim() : (v || ''));
              dispatch(setBodyMeasurements({
                chest: getVal(sizeData.chestBody),
                bodyLength: getVal(sizeData.length) || sizeData.length || '',
                sleeveLength: getVal(sizeData.sleeve) || sizeData.sleeve || '',
                armWidth: getVal(sizeData.armWidth) || sizeData.armWidth || '',
                waist: getVal(sizeData.waist) || sizeData.waist || '',
                shoulderToShoulder: getVal(sizeData.shoulder) || sizeData.shoulder || ''
              }));
            }
          }
        }
      }
    } catch (error) {
      // Error fetching product, continue with URL params
    }
  };
  
  // Check if it's Core Jaccket (for patches)
  // NOTE: Patches feature is currently disabled for all products (including core jacket)
  // const isCoreJacket = urlParams.productHandle && (
  //   urlParams.productHandle.toLowerCase().includes('core-jaccket') || 
  //   urlParams.productHandle.toLowerCase().includes('core-jacket')
  // );
  const isCoreJacket = false;
  
  // Check if it's from female collection (check collection param or URL)
  const isFemaleCollection = urlParams.collection && urlParams.collection.toLowerCase().includes('female');
  
  // Get the appropriate size chart
  const SIZE_CHART = isFemaleCollection ? FEMALE_SIZE_CHART : MALE_SIZE_CHART;
  
  // Get available sizes based on chart
  const availableSizes = Object.keys(SIZE_CHART);
  
  
  const toggleBottomSheet = () => {
    setIsBottomSheetVisible(!isBottomSheetVisible);
  };

  useEffect(() => {
    isDraggingRef.current = isDragging;
  }, [isDragging]);

  useEffect(() => {
    currentImageViewRef.current = currentImageView;
  }, [currentImageView]);

  const handleMouseDown = (e) => {
    if (currentImageView === 'back' && selectedPatches['Back Side'] !== null) {
      setIsDragging(true);
      isDraggingRef.current = true;
      e.preventDefault();
      e.stopPropagation();
    }
  };

  const handleTouchStart = (e) => {
    if (currentImageView === 'back' && selectedPatches['Back Side'] !== null) {
      setIsDragging(true);
      isDraggingRef.current = true;
      e.preventDefault();
      e.stopPropagation();
    }
  };

  const handleMouseMove = useCallback((e) => {
    if (!isDraggingRef.current || currentImageViewRef.current !== 'back') {
      return;
    }
    
    const imageWrapper = document.querySelector('.product-image-wrapper');
    if (!imageWrapper) {
      return;
    }
    
    const rect = imageWrapper.getBoundingClientRect();
    const clientX = e.clientX || (e.touches && e.touches[0]?.clientX);
    const clientY = e.clientY || (e.touches && e.touches[0]?.clientY);
    
    if (!clientX || !clientY) {
      return;
    }
    
    const x = ((clientX - rect.left) / rect.width) * 100;
    const y = ((clientY - rect.top) / rect.height) * 100;
    
    const constrainedX = Math.max(10, Math.min(90, x));
    const constrainedY = Math.max(10, Math.min(90, y));
    
    setBackSidePatchPosition({ x: constrainedX, y: constrainedY });
  }, []);

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
    isDraggingRef.current = false;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [backSidePatchPosition]);

  useEffect(() => {
    if (isDragging) {
      const moveHandler = (e) => {
        e.preventDefault();
        handleMouseMove(e);
      };
      const upHandler = () => handleMouseUp();
      
      document.addEventListener('mousemove', moveHandler, { passive: false });
      document.addEventListener('mouseup', upHandler);
      document.addEventListener('touchmove', moveHandler, { passive: false });
      document.addEventListener('touchend', upHandler);
      document.body.style.cursor = 'grabbing';
      document.body.style.userSelect = 'none';
      document.body.style.touchAction = 'none';
      
      return () => {
        document.removeEventListener('mousemove', moveHandler);
        document.removeEventListener('mouseup', upHandler);
        document.removeEventListener('touchmove', moveHandler);
        document.removeEventListener('touchend', upHandler);
        document.body.style.cursor = '';
        document.body.style.userSelect = '';
        document.body.style.touchAction = '';
      };
    }
  }, [isDragging, handleMouseMove, handleMouseUp]);

  
  const uploadImageToFirebase = async (imageDataUrl, fileName) => {
    try {
      
      const timestamp = Date.now();
      const uniqueFileName = `customized-back-${timestamp}-${fileName || 'patch.png'}`;
      
      
      const storageRef = ref(storage, `customized-images/${uniqueFileName}`);
      
      
      const snapshot = await uploadString(storageRef, imageDataUrl, 'data_url');
      
      
      const downloadURL = await getDownloadURL(snapshot.ref);
      
      return downloadURL;
    } catch (error) {
      return null;
    }
  };

  
  const generateTextPreviewImage = () => {
    if (!customText) {
      setTextPreviewImage(null);
      return;
    }

    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    
    
    const padding = 20;
    ctx.font = `bold ${textSize}px Arial`;
    const metrics = ctx.measureText(customText);
    const textWidth = metrics.width;
    const textHeight = parseInt(textSize, 10);
    
    canvas.width = textWidth + (padding * 2);
    canvas.height = textHeight + (padding * 2);
    
    
    ctx.font = `bold ${textSize}px Arial`;
    ctx.fillStyle = textColor;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    
    
    ctx.fillText(customText, canvas.width / 2, canvas.height / 2);
    
    
    const dataUrl = canvas.toDataURL('image/png');
    setTextPreviewImage(dataUrl);
  };

  
  const generateCustomizedBackImage = async () => {
    if (currentImageView !== 'back' || selectedPatches['Back Side'] === null) {
      return null;
    }

    return new Promise((resolve) => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      
      
      const backImage = new Image();
      backImage.crossOrigin = 'anonymous';
      backImage.src = '/images/Back side.png';
      
      backImage.onload = () => {
        canvas.width = backImage.width;
        canvas.height = backImage.height;
        
        
        ctx.drawImage(backImage, 0, 0);
        
        
        const patchImage = new Image();
        patchImage.crossOrigin = 'anonymous';
        
        let patchSrc = null;
        
        if (selectedPatches['Back Side'] === 'custom' && customPhoto) {
          patchSrc = customPhoto;
        } else if (selectedPatches['Back Side'] === 'text' && customText) {
          
          const patchX = (backSidePatchPosition.x / 100) * canvas.width;
          const patchY = (backSidePatchPosition.y / 100) * canvas.height;
          
          ctx.font = `bold ${textSize}px Arial`;
          ctx.fillStyle = textColor;
          ctx.textAlign = 'center';
          ctx.textBaseline = 'middle';
          ctx.fillText(customText, patchX, patchY);
          
          const dataUrl = canvas.toDataURL('image/png');
          setCustomizedBackImage(dataUrl);
          resolve(dataUrl);
          return;
        } else {
          patchSrc = getPatchDesignsForPlacement('Back Side')[selectedPatches['Back Side']]?.image;
        }
        
        if (patchSrc) {
          patchImage.onload = () => {
            
            let drawWidth = patchImage.width;
            let drawHeight = patchImage.height;
            
            
            if (selectedPatches['Back Side'] === 'custom' && customPhoto) {
              
              
              
              
              const imageWrapper = document.querySelector('.product-image-wrapper');
              if (imageWrapper) {
                // eslint-disable-next-line no-unused-vars
                const wrapperRect = imageWrapper.getBoundingClientRect();
                const imageRect = document.querySelector('.product-image')?.getBoundingClientRect();
                if (imageRect) {
                  
                  
                  
                  const screenToCanvasScale = canvas.width / imageRect.width;
                  drawWidth = customImageSize * screenToCanvasScale;
                  drawHeight = (patchImage.height / patchImage.width) * drawWidth; 
                } else {
                  
                  const scaleFactor = customImageSize / 50;
                  drawWidth = patchImage.width * scaleFactor;
                  drawHeight = patchImage.height * scaleFactor;
                }
              } else {
                
                const scaleFactor = customImageSize / 50;
                drawWidth = patchImage.width * scaleFactor;
                drawHeight = patchImage.height * scaleFactor;
              }
            }
            
            const patchX = (backSidePatchPosition.x / 100) * canvas.width - (drawWidth / 2);
            const patchY = (backSidePatchPosition.y / 100) * canvas.height - (drawHeight / 2);
            
            
            ctx.drawImage(patchImage, patchX, patchY, drawWidth, drawHeight);
            
            const dataUrl = canvas.toDataURL('image/png');
            setCustomizedBackImage(dataUrl);
            resolve(dataUrl);
          };
          patchImage.src = patchSrc;
        } else {
          const dataUrl = canvas.toDataURL('image/png');
          setCustomizedBackImage(dataUrl);
          resolve(dataUrl);
        }
      };
    });
  };

  
  useEffect(() => {
    if (selectedPatches['Back Side'] === 'text') {
      generateTextPreviewImage();
    } else {
      setTextPreviewImage(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customText, textColor, textSize, selectedPatches['Back Side']]);

  
  useEffect(() => {
    if (currentImageView === 'back' && selectedPatches['Back Side'] !== null && !isDragging) {
      const timer = setTimeout(() => {
        generateCustomizedBackImage();
      }, 100); 
      return () => clearTimeout(timer);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [backSidePatchPosition.x, backSidePatchPosition.y, selectedPatches['Back Side'], customText, textColor, textSize, customPhoto, customImageSize, currentImageView, isDragging]);
  
  
  // Get base price from Shopify or use default
  const basePrice = urlParams.price ? parseFloat(urlParams.price) : 199;
  const customImagePrice = 20;
  
  // Calculate size extra price: $10 for each size increment above L
  // XL = +$10, 2XL = +$20, 3XL = +$30, 4XL = +$40, 5XL = +$50
  const getSizeExtraPrice = (size) => {
    if (!size) return 0;
    const sizeOrder = ['XS', 'S', 'M', 'L', 'XL', '2XL', '3XL', '4XL', '5XL'];
    const sizeIndex = sizeOrder.indexOf(size);
    const lIndex = sizeOrder.indexOf('L');
    
    if (sizeIndex > lIndex) {
      const increments = sizeIndex - lIndex;
      return increments * 10;
    }
    return 0;
  };
  
  const sizeExtraPrice = getSizeExtraPrice(selectedJacketSize);
  
  // Get Shopify image URL and format it properly
  const getShopifyImageUrl = () => {
    if (!urlParams.image) return null;
    // Handle relative URLs (//cdn.shopify.com -> https://cdn.shopify.com)
    if (urlParams.image.startsWith('//')) {
      return `https:${urlParams.image}`;
    }
    // Handle absolute URLs
    if (urlParams.image.startsWith('http://') || urlParams.image.startsWith('https://')) {
      return urlParams.image;
    }
    // Handle relative paths
    return urlParams.image;
  };
  
  const shopifyImageUrl = getShopifyImageUrl();
  
  // Helper to get first value from range (e.g. "31-32" -> "31", "14-14.5" -> "14")
  const getFirstValue = (val) => {
    if (!val) return '';
    const str = String(val).trim();
    return str.includes('-') ? str.split('-')[0].trim() : str;
  };
  
  // Returns measurements object from size chart for pre-population
  const getMeasurementsFromSize = (size) => {
    if (!SIZE_CHART[size]) return null;
    const sizeData = SIZE_CHART[size];
    return {
      chest: getFirstValue(sizeData.chestBody),
      bodyLength: getFirstValue(sizeData.length) || sizeData.length || '',
      sleeveLength: getFirstValue(sizeData.sleeve) || sizeData.sleeve || '',
      armWidth: getFirstValue(sizeData.armWidth) || sizeData.armWidth || '',
      waist: getFirstValue(sizeData.waist) || sizeData.waist || '',
      shoulderToShoulder: getFirstValue(sizeData.shoulder) || sizeData.shoulder || ''
    };
  };
  
  // Function to populate body measurements based on selected size
  const populateMeasurementsFromSize = (size) => {
    const measurements = getMeasurementsFromSize(size);
    if (measurements) {
      setTempBodyMeasurements(measurements);
    }
  };
  
  // Handle size selection - just select the size, don't navigate
  const handleSizeSelection = (size) => {
    dispatch(setSelectedJacketSize(size));
    // Don't auto-populate - let user enter measurements manually
  };
  
  // Check if body measurements exceed selected jacket size
  const checkMeasurementsVsSelectedSize = () => {
    if (!selectedJacketSize || !SIZE_CHART[selectedJacketSize]) return null;
    
    // eslint-disable-next-line no-unused-vars
    const selectedSizeData = SIZE_CHART[selectedJacketSize];
    const measurements = tempBodyMeasurements;
    
    // Check all measurements
    const userChest = parseFloat(measurements.chest);
    const userBodyLength = parseFloat(measurements.bodyLength);
    const userSleeveLength = parseFloat(measurements.sleeveLength);
    const userShoulder = parseFloat(measurements.shoulderToShoulder);
    
    // If no measurements entered, return null
    if ((!userChest || isNaN(userChest)) && (!userBodyLength || isNaN(userBodyLength)) && 
        (!userSleeveLength || isNaN(userSleeveLength)) && (!userShoulder || isNaN(userShoulder))) {
      return null;
    }
    
    // Use available sizes from the current size chart instead of hardcoded list
    const sizeOrder = availableSizes;
    let matchedSizes = [];
    
    // Check chest measurement
    if (userChest && !isNaN(userChest)) {
      let chestMatched = false;
      for (const size of sizeOrder) {
        if (SIZE_CHART[size]) {
          const sizeChestRange = SIZE_CHART[size].chestBody.split('-');
          const sizeChestMin = parseFloat(sizeChestRange[0]);
          const sizeChestMax = parseFloat(sizeChestRange[1] || sizeChestRange[0]);
          
          if (userChest >= sizeChestMin && userChest <= sizeChestMax) {
            matchedSizes.push(size);
            chestMatched = true;
            break;
          }
        }
      }
      // If chest is larger than all sizes, use largest
      if (!chestMatched) {
        const lastSize = sizeOrder[sizeOrder.length - 1];
        if (SIZE_CHART[lastSize]) {
          const lastSizeChestMax = parseFloat(SIZE_CHART[lastSize].chestBody.split('-')[1] || SIZE_CHART[lastSize].chestBody.split('-')[0]);
          if (userChest > lastSizeChestMax) {
            matchedSizes.push(lastSize);
          }
        }
      }
    }
    
    // Check body length measurement
    if (userBodyLength && !isNaN(userBodyLength)) {
      let lengthMatched = false;
      for (const size of sizeOrder) {
        if (SIZE_CHART[size]) {
          const sizeLength = parseFloat(SIZE_CHART[size].length);
          if (userBodyLength <= sizeLength + 0.5) {
            matchedSizes.push(size);
            lengthMatched = true;
            break;
          }
        }
      }
      // If length is larger than all sizes, use largest
      if (!lengthMatched) {
        const lastSize = sizeOrder[sizeOrder.length - 1];
        if (SIZE_CHART[lastSize]) {
          const lastSizeLength = parseFloat(SIZE_CHART[lastSize].length);
          if (userBodyLength > lastSizeLength) {
            matchedSizes.push(lastSize);
          }
        }
      }
    }
    
    // Check sleeve length measurement
    if (userSleeveLength && !isNaN(userSleeveLength)) {
      let sleeveMatched = false;
      for (const size of sizeOrder) {
        if (SIZE_CHART[size]) {
          const sizeSleeve = parseFloat(SIZE_CHART[size].sleeve);
          if (userSleeveLength <= sizeSleeve + 0.5) {
            matchedSizes.push(size);
            sleeveMatched = true;
            break;
          }
        }
      }
      // If sleeve is larger than all sizes, use largest
      if (!sleeveMatched) {
        const lastSize = sizeOrder[sizeOrder.length - 1];
        if (SIZE_CHART[lastSize]) {
          const lastSizeSleeve = parseFloat(SIZE_CHART[lastSize].sleeve);
          if (userSleeveLength > lastSizeSleeve) {
            matchedSizes.push(lastSize);
          }
        }
      }
    }
    
    // Check shoulder measurement
    if (userShoulder && !isNaN(userShoulder)) {
      let shoulderMatched = false;
      for (const size of sizeOrder) {
        if (SIZE_CHART[size]) {
          const sizeShoulder = parseFloat(SIZE_CHART[size].shoulder);
          if (userShoulder <= sizeShoulder + 0.5) {
            matchedSizes.push(size);
            shoulderMatched = true;
            break;
          }
        }
      }
      // If shoulder is larger than all sizes, use largest
      if (!shoulderMatched) {
        const lastSize = sizeOrder[sizeOrder.length - 1];
        if (SIZE_CHART[lastSize]) {
          const lastSizeShoulder = parseFloat(SIZE_CHART[lastSize].shoulder);
          if (userShoulder > lastSizeShoulder) {
            matchedSizes.push(lastSize);
          }
        }
      }
    }
    
    // Find the largest matched size
    if (matchedSizes.length === 0) return null;
    
    let matchedSize = null;
    let maxIndex = -1;
    for (const size of matchedSizes) {
      const index = sizeOrder.indexOf(size);
      if (index > maxIndex) {
        maxIndex = index;
        matchedSize = size;
      }
    }
    
    if (!matchedSize) return null;
    
    const selectedSizeIndex = sizeOrder.indexOf(selectedJacketSize);
    const matchedSizeIndex = sizeOrder.indexOf(matchedSize);
    
    // If matched size is larger than selected size
    if (matchedSizeIndex > selectedSizeIndex) {
      const extraPrice = getSizeExtraPrice(matchedSize) - getSizeExtraPrice(selectedJacketSize);
      return {
        matchedSize,
        extraPrice,
        exceeds: true
      };
    }
    
    return null;
  };
  
  const measurementWarning = checkMeasurementsVsSelectedSize();
  
  // Calculate total price including measurement extra charge
  const measurementExtraPrice = measurementWarning && measurementWarning.exceeds ? measurementWarning.extraPrice : 0;
  const totalPrice = basePrice + (customPhoto ? customImagePrice : 0) + sizeExtraPrice + measurementExtraPrice;
  
  // Apply changes from body size modal
  const handleApplyBodySizeChanges = () => {
    dispatch(setBodyMeasurements(tempBodyMeasurements));
    setIsBodySizeModalVisible(false);
  };
  
  // Initialize temp values when body size modal opens
  useEffect(() => {
    if (isBodySizeModalVisible) {
      // Pre-populate from bodyMeasurements (set when user clicked Next from jacket size modal)
      if (bodyMeasurements.chest || bodyMeasurements.bodyLength || bodyMeasurements.sleeveLength || bodyMeasurements.armWidth || bodyMeasurements.waist || bodyMeasurements.shoulderToShoulder) {
        setTempBodyMeasurements({...bodyMeasurements});
      } else {
        setTempBodyMeasurements({
          chest: '',
          bodyLength: '',
          sleeveLength: '',
          armWidth: '',
          waist: '',
          shoulderToShoulder: ''
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isBodySizeModalVisible, selectedJacketSize]);

  
  const handleCustomPhotoUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        dispatch(setCustomPhoto(e.target.result));
        dispatch(setSelectedPatches({
          ...selectedPatches,
          'Back Side': 'custom'
        }));
      };
      reader.readAsDataURL(file);
    }
  };

  
  const removeCustomPhoto = () => {
    dispatch(setCustomPhoto(null));
    dispatch(setSelectedPatches({
      ...selectedPatches,
      'Back Side': null
    }));
  };

  
  
  // Helper function to check if a measurement matches standard size value
  const isMeasurementCustomized = (measurementKey, measurementValue) => {
    // If no jacket size selected, consider all measurements as customized
    if (!selectedJacketSize || !SIZE_CHART[selectedJacketSize]) {
      return measurementValue && measurementValue !== '';
    }
    
    // If measurement value is empty, it's not customized
    if (!measurementValue || measurementValue === '') {
      return false;
    }
    
    const sizeData = SIZE_CHART[selectedJacketSize];
    const standardValue = (() => {
      switch (measurementKey) {
        case 'chest':
          return getFirstValue(sizeData.chestBody) || null;
        case 'bodyLength':
          return getFirstValue(sizeData.length) || sizeData.length || null;
        case 'sleeveLength':
          return getFirstValue(sizeData.sleeve) || sizeData.sleeve || null;
        case 'armWidth':
          return getFirstValue(sizeData.armWidth) || sizeData.armWidth || null;
        case 'waist':
          return getFirstValue(sizeData.waist) || sizeData.waist || null;
        case 'shoulderToShoulder':
          return getFirstValue(sizeData.shoulder) || sizeData.shoulder || null;
        default:
          return null;
      }
    })();
    
    // If no standard value exists, consider it customized
    if (!standardValue) {
      return true;
    }
    
    // Compare values (handle floating point comparison)
    const userValue = parseFloat(measurementValue);
    const standardNumValue = parseFloat(standardValue);
    
    // If parsing fails, do string comparison
    if (isNaN(userValue) || isNaN(standardNumValue)) {
      return measurementValue !== standardValue;
    }
    
    // Allow small tolerance for floating point comparison (0.1 inches)
    return Math.abs(userValue - standardNumValue) > 0.1;
  };

  const handleCheckout = async () => {
    if (!urlParams.variant_id || !urlParams.shop) {
      setIsLoading(false);
      return;
    }
  
    setIsLoading(true);
  
    try {
      // 1. Check if any body measurements are customized
      const hasCustomizedMeasurements = 
        (bodyMeasurements.chest && isMeasurementCustomized('chest', bodyMeasurements.chest)) ||
        (bodyMeasurements.bodyLength && isMeasurementCustomized('bodyLength', bodyMeasurements.bodyLength)) ||
        (bodyMeasurements.sleeveLength && isMeasurementCustomized('sleeveLength', bodyMeasurements.sleeveLength)) ||
        (bodyMeasurements.armWidth && isMeasurementCustomized('armWidth', bodyMeasurements.armWidth)) ||
        (bodyMeasurements.waist && isMeasurementCustomized('waist', bodyMeasurements.waist)) ||
        (bodyMeasurements.shoulderToShoulder && isMeasurementCustomized('shoulderToShoulder', bodyMeasurements.shoulderToShoulder));
      
      // 2. If any measurements are customized, validate ALL fields are filled
      if (hasCustomizedMeasurements) {
        const requiredFields = ['chest', 'bodyLength', 'sleeveLength', 'armWidth', 'waist', 'shoulderToShoulder'];
        const missingFields = requiredFields.filter(field => {
          const value = bodyMeasurements[field];
          return !value || value.trim() === '';
        });
        
        if (missingFields.length > 0) {
          alert('Please fill all body measurement fields before checkout.');
          setIsLoading(false);
          return;
        }
      }
      
      // 3. Build properties object
      const properties = {};
      
      if (selectedWeight) {
        properties['Leather_Type'] = selectedWeight === 'light' ? 'Light weight' : 'Heavy weight';
      }
      
      // 4. Logic: If body measurements customized, show only measurements (not jacket size)
      // If only jacket size selected, show only jacket size
      if (hasCustomizedMeasurements) {
        // Show only body measurements (not jacket size)
        if (bodyMeasurements.chest) {
          properties['Chest'] = `${bodyMeasurements.chest} inches`;
        }
        if (bodyMeasurements.bodyLength) {
          properties['Body_Length'] = `${bodyMeasurements.bodyLength} inches`;
        }
        if (bodyMeasurements.sleeveLength) {
          properties['Sleeve_Length'] = `${bodyMeasurements.sleeveLength} inches`;
        }
        if (bodyMeasurements.armWidth) {
          properties[isFemaleCollection ? 'Hip' : 'Arm_Width'] = `${bodyMeasurements.armWidth} inches`;
        }
        if (bodyMeasurements.waist) {
          properties['Waist'] = `${bodyMeasurements.waist} inches`;
        }
        if (bodyMeasurements.shoulderToShoulder) {
          properties['Shoulder_To_Shoulder'] = `${bodyMeasurements.shoulderToShoulder} inches`;
        }
      } else {
        // Only jacket size selected - show only jacket size
        if (selectedJacketSize) {
          properties['Jacket_Size'] = selectedJacketSize;
        }
      }
      
      // Add patch properties
      Object.keys(selectedPatches).forEach(placement => {
        const patchIndex = selectedPatches[placement];
        if (patchIndex !== null && patchIndex !== undefined) {
          if (placement === 'Back Side' && patchIndex === 'custom' && customPhoto) {
            properties[`Patch_${placement}`] = 'Custom Upload';
            properties[`_Custom_Patch_Position_X`] = `${backSidePatchPosition.x}%`;
            properties[`_Custom_Patch_Position_Y`] = `${backSidePatchPosition.y}%`;
          } else if (placement === 'Back Side' && patchIndex === 'text' && customText) {
            properties[`Patch_${placement}`] = 'Custom Text';
            properties[`_Custom_Text`] = customText;
            properties[`_Text_Color`] = textColor;
            properties[`_Text_Size`] = `${textSize}px`;
            properties[`_Text_Position_X`] = `${backSidePatchPosition.x}%`;
            properties[`_Text_Position_Y`] = `${backSidePatchPosition.y}%`;
          } else if (typeof patchIndex === 'number') {
            const patchDesigns = getPatchDesignsForPlacement(placement);
            if (patchDesigns[patchIndex] && patchDesigns[patchIndex].image) {
              properties[`Patch_${placement}`] = `Design ${patchIndex + 1}`;
            }
          }
        }
      });
      
      // Upload images to Firebase first (if needed)
      if (selectedPatches['Back Side'] === 'custom' && customPhoto && customPhoto.startsWith('data:')) {
        const uploadedUrl = await uploadImageToFirebase(customPhoto, 'original-custom-patch.png');
        if (uploadedUrl) {
          properties[`_Custom_Photo_URL`] = uploadedUrl;
        }
      }
      
      if (selectedPatches['Back Side'] !== null) {
        // Ensure you wait for image generation if it hasn't happened yet
        let imageToUpload = customizedBackImage || await generateCustomizedBackImage();
        if (imageToUpload) {
          const uploadedUrl = await uploadImageToFirebase(imageToUpload, 'customized-back.png');
          if (uploadedUrl) {
            properties[`_Customized_Back_Image_URL`] = uploadedUrl;
          }
        }
      }
  
      // 2. Clean and validate variant_id — POST cart/add on primary domain (shop.url) so /cart on ferraofficial.com sees the line item
      const cartOrigin = getStorefrontOrigin(urlParams);
      if (!cartOrigin) {
        setIsLoading(false);
        return;
      }

      // Get original variant_id from URL params
      const rawVariantId = urlParams.variant_id;
      
      // Clean the variant_id: remove any whitespace, non-numeric characters (except minus sign)
      const cleanedVariantId = String(rawVariantId).trim().replace(/[^\d-]/g, '');
      
      // Convert to integer and validate
      const variantId = parseInt(cleanedVariantId, 10);
      
      // Validate that we have a valid integer
      if (isNaN(variantId) || variantId <= 0) {
        setIsLoading(false);
        return;
      }
      
      // Same-origin (e.g. customize app hosted on ferraofficial.com): AJAX Cart API sets the storefront cart cookie reliably, then redirect to /cart.
      const isSameStorefrontOrigin =
        typeof window !== 'undefined' && window.location.origin === cartOrigin;
      if (isSameStorefrontOrigin) {
        try {
          const item = { id: variantId, quantity: 1 };
          if (properties && Object.keys(properties).length > 0) {
            item.properties = properties;
          }
          const res = await fetch(`${cartOrigin}/cart/add.js`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Accept: 'application/json'
            },
            credentials: 'include',
            body: JSON.stringify({ items: [item] })
          });
          if (res.ok) {
            window.location.href = `${cartOrigin}/cart`;
            return;
          }
        } catch {
          /* fall through to classic form POST */
        }
      }

      // Cross-origin (localhost / Vercel app): full navigation POST to storefront /cart/add (matches native product form fields)
      const form = document.createElement('form');
      form.method = 'POST';
      form.action = `${cartOrigin}/cart/add`;
      form.style.display = 'none';
      form.acceptCharset = 'UTF-8';
      form.target = '_self';

      const utf8Input = document.createElement('input');
      utf8Input.type = 'hidden';
      utf8Input.name = 'utf8';
      utf8Input.value = '\u2713';
      form.appendChild(utf8Input);

      const formTypeInput = document.createElement('input');
      formTypeInput.type = 'hidden';
      formTypeInput.name = 'form_type';
      formTypeInput.value = 'product';
      form.appendChild(formTypeInput);

      const variantInput = document.createElement('input');
      variantInput.type = 'hidden';
      variantInput.name = 'id';
      variantInput.value = String(variantId);
      form.appendChild(variantInput);

      const quantityInput = document.createElement('input');
      quantityInput.type = 'hidden';
      quantityInput.name = 'quantity';
      quantityInput.value = '1';
      form.appendChild(quantityInput);

      const returnInput = document.createElement('input');
      returnInput.type = 'hidden';
      returnInput.name = 'return_to';
      returnInput.value = '/cart';
      form.appendChild(returnInput);

      Object.keys(properties).forEach((key) => {
        const propInput = document.createElement('input');
        propInput.type = 'hidden';
        propInput.name = `properties[${key}]`;
        propInput.value = String(properties[key]);
        form.appendChild(propInput);
      });

      document.body.appendChild(form);
      form.submit();
      
    } catch (error) {
      setIsLoading(false);
    }
  };
    return (
    <div>

    <div className="container">
      
      {/* Top Navigation */}
      <div className="top-nav">
        {/* Back Button */}
        <button 
          className="back-button"
          onClick={() => {
            // Try to go back to the specific product page on Shopify
            const storeOrigin = getStorefrontOrigin(urlParams);
            if (storeOrigin && urlParams.productHandle) {
              window.location.href = `${storeOrigin}/products/${urlParams.productHandle}`;
            } else if (storeOrigin) {
              window.location.href = `${storeOrigin}/collections/all`;
            } else if (document.referrer && document.referrer !== window.location.href) {
              // Try to go back to the previous page (where user came from)
              window.history.back();
            } else {
              // Fallback if shop is not available
              window.location.href = '/';
            }
          }}
        >
          <span className="arrow-left">←</span>
        </button>
        
        {/* Logo */}
        <div className="logo-container">
          <img src="/images/logo.png" alt="Ferra" className="logo-image" />
        </div>
        
     
      </div>
      <div className="flex-container justify-between ">
      {/* Details Title */}
      <div>
        <h1 className="details-title">DETAILS</h1>
      </div>
         {/* Change Product Button */}
         <button 
          className="change-product-button"
          onClick={() => {
            // Redirect to Shopify store - products/collections page
            const originNav = getStorefrontOrigin(urlParams);
            if (originNav) {
              window.location.href = `${originNav}/collections/all`;
            } else {
              // Fallback if shop is not available
              window.location.href = '/';
            }
          }}
        >
          <span className="change-product-text">Change product</span>
        </button>
      </div>
      {/* Desktop Layout Wrapper */}
      <div className="desktop-wrapper">
        {/* Left Side - Product Images */}
        <div className="desktop-left-content">
          {/* Product Images */}
          <div className="product-images-container">
            <div className="product-image-wrapper">
              {/* Navigation Arrows - Only show for Core Jacket */}
              {isCoreJacket && (
                <>
                  <button 
                    className="image-nav-arrow image-nav-arrow-left"
                    onClick={() => setCurrentImageView('front')}
                    disabled={currentImageView === 'front'}
                    aria-label="Show front view"
                  >
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M15 18L9 12L15 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </button>
                  
                  <button 
                    className="image-nav-arrow image-nav-arrow-right"
                    onClick={() => setCurrentImageView('back')}
                    disabled={currentImageView === 'back'}
                    aria-label="Show back view"
                  >
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M9 18L15 12L9 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </button>
                </>
              )}
              
              {/* Product Image */}
              <div className="product-image-container">
                {isCoreJacket ? (
                  <img
                    src={currentImageView === 'front' ? '/images/front side.png' : '/images/Back side.png'}
                    alt={currentImageView === 'front' ? 'Front View' : 'Back View'}
                    className="product-image"
                  />
                ) : (
                  <img
                    src={shopifyImageUrl || '/images/front side.png'}
                    alt={urlParams.title || 'Product Image'}
                    className="product-image"
                  />
                )}
              </div>
              
              {/* Front View Patches - ZIP, Right Arm, Left Arm - Only for Core Jacket */}
              {isCoreJacket && currentImageView === 'front' && (
                <>
                  <div className="number-overlay patch-overlay-front patch-overlay-zip">
                    {selectedPatches['ZIP'] !== null && (
                      <div className="patch-preview">
                        <img 
                          src={getPatchDesignsForPlacement('ZIP')[selectedPatches['ZIP']]?.image}
                          alt="ZIP Patch"
                          className="patch-preview-image"
                        />
                      </div>
                    )}
                  </div>
                  
                  <div className="number-overlay patch-overlay-front patch-overlay-right-arm">
                    {selectedPatches['Right Arm'] !== null && (
                      <div className="patch-preview">
                        <img 
                          src={getPatchDesignsForPlacement('Right Arm')[selectedPatches['Right Arm']]?.image}
                          alt="Right Arm Patch"
                          className="patch-preview-image"
                        />
                      </div>
                    )}
                  </div>
                  
                  <div className="number-overlay patch-overlay-front patch-overlay-left-arm">
                    {selectedPatches['Left Arm'] !== null && (
                      <div className="patch-preview">
                        <img 
                          src={getPatchDesignsForPlacement('Left Arm')[selectedPatches['Left Arm']]?.image}
                          alt="Left Arm Patch"
                          className="patch-preview-image"
                        />
                      </div>
                    )}
                  </div>
                </>
              )}
              
              {/* Back View Patch - Back Side (Draggable) - Only for Core Jacket */}
              {isCoreJacket && currentImageView === 'back' && (
                <div 
                  className={`number-overlay patch-overlay-back patch-overlay-back-side draggable-patch ${isDragging ? 'dragging' : ''}`}
                  style={{
                    left: `${backSidePatchPosition.x}%`,
                    top: `${backSidePatchPosition.y}%`,
                    transform: 'translate(-50%, -50%)',
                    cursor: selectedPatches['Back Side'] !== null ? (isDragging ? 'grabbing' : 'grab') : 'default',
                    pointerEvents: selectedPatches['Back Side'] !== null ? 'auto' : 'none',
                    position: 'absolute'
                  }}
                  onMouseDown={handleMouseDown}
                  onTouchStart={handleTouchStart}
                >
                  {selectedPatches['Back Side'] !== null && (
                    <div className="patch-preview">
                      {selectedPatches['Back Side'] === 'custom' && customPhoto ? (
                        <img 
                          src={customPhoto}
                          alt="Custom Patch"
                          className="patch-preview-image"
                          style={{
                            width: `${customImageSize}px`,
                            height: `${customImageSize}px`,
                            maxWidth: `${customImageSize}px`,
                            maxHeight: `${customImageSize}px`
                          }}
                          draggable={false}
                        />
                      ) : selectedPatches['Back Side'] === 'text' && customText && textPreviewImage ? (
                        <img 
                          src={textPreviewImage}
                          alt="Text Preview"
                          className="patch-preview-image"
                          draggable={false}
                        />
                      ) : selectedPatches['Back Side'] === 'text' && customText ? (
                        <div 
                          className="patch-preview-text"
                          style={{ 
                            color: textColor, 
                            fontSize: `${textSize}px`,
                            fontWeight: 'bold'
                          }}
                        >
                          {customText}
                        </div>
                      ) : (
                        <img 
                          src={getPatchDesignsForPlacement('Back Side')[selectedPatches['Back Side']]?.image}
                          alt="Back Side Patch"
                          className="patch-preview-image"
                          draggable={false}
                        />
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
        
        {/* Right Side - Product Details */}
        <div className="desktop-right-content">
          {/* Leather Type Section */}
          <div className="leather-type-section">
        <span className="leather-type-label">Leather Type</span>
        <div className="weight-toggle-container">
          <button
            className={`weight-toggle ${selectedWeight === 'light' ? 'weight-toggle-active' : ''}`}
            onClick={() => dispatch(setSelectedWeight('light'))}
          >
            <span className={`weight-toggle-text ${selectedWeight === 'light' ? 'weight-toggle-text-active' : ''}`}>
              Light weight
            </span>
          </button>
          <button
            className={`weight-toggle ${selectedWeight === 'heavy' ? 'weight-toggle-active' : ''}`}
            onClick={() => dispatch(setSelectedWeight('heavy'))}
          >
            <span className={`weight-toggle-text ${selectedWeight === 'heavy' ? 'weight-toggle-text-active' : ''}`}>
              Heavy weight
            </span>
          </button>
        </div>
        
        {/* Leather Type Description */}
        <div className="leather-type-description">
          {selectedWeight === 'light' ? (
            <div>
              <p className="leather-type-subtitle">Key Features:</p>
              <ul className="leather-type-features">
                <li>Lightweight and breathable</li>
                <li>Naturally soft and supple texture</li>
                <li>Fine grain surface for a premium feel</li>
              </ul>
            </div>
          ) : (
            <div>
              <p className="leather-type-subtitle">Key Features:</p>
              <ul className="leather-type-features">
                <li>Thick, durable, and long-lasting</li>
                <li>Resistant to scratches and stretching</li>
                <li>Natural grain texture for a bold look</li>
              </ul>
            </div>
          )}
        </div>
      </div>
      
      {/* Fit Selection Section */}
      <div className="fit-selection-section">
        <span className="fit-selection-label">Fit Type</span>
        <div className="fit-toggle-container">
          <button
            className={`fit-toggle ${selectedFit === 'classic' ? 'fit-toggle-active' : ''}`}
            onClick={() => dispatch(setSelectedFit('classic'))}
          >
            <span className={`fit-toggle-text ${selectedFit === 'classic' ? 'fit-toggle-text-active' : ''}`}>
              Classic Fit
            </span>
          </button>
          <button
            className={`fit-toggle ${selectedFit === 'slim' ? 'fit-toggle-active' : ''}`}
            onClick={() => dispatch(setSelectedFit('slim'))}
          >
            <span className={`fit-toggle-text ${selectedFit === 'slim' ? 'fit-toggle-text-active' : ''}`}>
              Slim Fit
            </span>
          </button>
        </div>
      </div>
     
      {/* Product Name and Price */}
      <div className="product-info-section">
        <div className="flex-container justify-between  gap-20 w-full">
        <h2 className="product-name">{urlParams.title || 'LEATHER PATCHES JACKET'}</h2>
        <span className="product-price">${totalPrice}</span>

          </div>
        </div>
        <div className="flex-item">
        <div className="price-container">
          {selectedJacketSize && (
            <span className="selected-size-display">Size: {selectedJacketSize}</span>
          )}
          {customPhoto && (
            <span className="custom-image-price">+$20</span>
          )}
          {sizeExtraPrice > 0 && (
            <span className="custom-image-price">+${sizeExtraPrice} (Size {selectedJacketSize})</span>
          )}
          {measurementExtraPrice > 0 && (
            <span className="custom-image-price">+${measurementExtraPrice} (Custom Measurements)</span>
          )}
        </div>
      </div>
      
          {/* Description Section */}
          <div className="description-section">
            {(() => {
              if (!urlParams.description) {
                return <div className="description-text">Lorem Ipsum<br />Lorem ipsum dolor sit amet,</div>;
              }
              
              // Parse description to separate Design details from other factors
              const description = urlParams.description;
              const lines = description.split('\n').filter(line => line.trim());
              
              let designDetails = '';
              const otherFactors = [];
              
              let foundDesignDetails = false;
              let currentSection = '';
              
              lines.forEach((line, index) => {
                const trimmedLine = line.trim();
                
                // Check if this is "Design details:" line
                if (trimmedLine.toLowerCase().includes('design details') || trimmedLine.toLowerCase().startsWith('design details')) {
                  foundDesignDetails = true;
                  currentSection = 'design';
                  // Extract text after "Design details:" or "Design details"
                  const detailsText = trimmedLine.replace(/^.*?design details:?\s*/i, '').trim();
                  if (detailsText) {
                    designDetails = detailsText;
                  }
                } else if (foundDesignDetails && currentSection === 'design') {
                  // Continue collecting design details until we hit another label
                  if (trimmedLine.includes(':') && !trimmedLine.toLowerCase().includes('design')) {
                    // This is likely a new section
                    currentSection = 'other';
                    otherFactors.push(trimmedLine);
                  } else if (trimmedLine) {
                    designDetails += (designDetails ? ' ' : '') + trimmedLine;
                  }
                } else {
                  // Other factors
                  if (trimmedLine) {
                    otherFactors.push(trimmedLine);
                  }
                }
              });
              
              // If no design details found, try to extract from the beginning
              if (!designDetails && lines.length > 0) {
                // Look for a paragraph that doesn't start with a label
                const firstLine = lines[0].trim();
                if (!firstLine.includes(':') || firstLine.toLowerCase().includes('design')) {
                  designDetails = firstLine.replace(/^.*?design details:?\s*/i, '').trim();
                  if (designDetails) {
                    otherFactors.shift(); // Remove it from other factors
                  }
                }
              }
              
              return (
                <div className="description-content">
                  {designDetails && (
                    <div className="design-details-section">
                      <div className="design-details-label">Design details:</div>
                      <div className="design-details-text">{designDetails}</div>
                    </div>
                  )}
                  
                  {otherFactors.length > 0 && (
                    <div className="other-factors-section">
                      {otherFactors.map((factor, index) => {
                        if (!factor.trim()) return null;
                        
                        // Split by colon if present
                        const colonIndex = factor.indexOf(':');
                        if (colonIndex > 0) {
                          const label = factor.substring(0, colonIndex).trim();
                          const value = factor.substring(colonIndex + 1).trim();
                          
                          // Skip if it's design details (already shown)
                          if (label.toLowerCase().includes('design')) {
                            return null;
                          }
                          
                          // If there's no value after the colon, treat the whole line as plain text
                          // This covers cases like the intro paragraph ending with "Key Features:"
                          if (!value) {
                            return (
                              <div key={index} className="factor-item factor-item-plain">
                                {factor.trim()}
                              </div>
                            );
                          }
                          
                          return (
                            <div key={index} className="factor-item">
                              <span className="factor-label">{label}:</span>
                              <span className="factor-value">{value}</span>
                            </div>
                          );
                        } else {
                          // No colon, treat as plain text
                          return (
                            <div key={index} className="factor-item factor-item-plain">
                              {factor}
                            </div>
                          );
                        }
                      })}
                    </div>
                  )}
                </div>
              );
            })()}
          </div>
          
          {/* Checkout Button - Right after description */}
      
        </div>
      </div>
      
    </div>
      
      {/* Mobile Bottom Trigger - Only visible on mobile */}
      {/* <div 
        className="mobile-bottom-trigger"
        onClick={toggleBottomSheet}
        style={{ display: window.innerWidth < 1024 ? 'block' : 'none' }}
      /> */}
      
      {/* Bottom Features Sheet */}
      <div 
        className={`bottom-sheet ${isBottomSheetVisible ? 'show' : 'hide'}`}
        onClick={toggleBottomSheet}
      >
        {/* Handle */}
        {/* <div className="sheet-handle" /> */}
        
        {/* Features Header */}
        <div className="features-header">
          <button 
            className="features-title-button"
            onClick={(e) => {
              e.stopPropagation();
              toggleBottomSheet();
            }}
          >
            <span className="customise-arrow">↑</span>
            CUSTOMISE
          </button>
          {/* <span className="full-list-text">full list</span> */}
        </div>
        
        {/* Features List */}
        <div className="features-list">
          <button 
            className="feature-item feature-button-hover"
            onClick={(e) => {
              e.stopPropagation();
              setIsJacketSizeModalVisible(true);
            }}
          >
            <div className="feature-step-number">1</div>
            {selectedJacketSize && <div className="feature-checkmark">✓</div>}
            <img src="/images/jacketsize.png" alt="Jacket Size" className="feature-icon" />
            <span className="feature-text">Jacket Size {selectedJacketSize ? `(${selectedJacketSize})` : ''}</span>
          </button>
          
          <button 
            className="feature-item feature-button-hover"
            onClick={(e) => {
              e.stopPropagation();
              setCameFromJacketSizeModal(false);
              setIsBodySizeModalVisible(true);
            }}
          >
            <div className="feature-step-number">2</div>
            {bodyMeasurements.chest && <div className="feature-checkmark">✓</div>}
            <img src="/images/bodysize.png" alt="Body Measurements" className="feature-icon" />
            <span className="feature-text">Body Measurements</span>
          </button>
          
          {isCoreJacket && (
            <button 
              className="feature-item"
              onClick={(e) => {
                e.stopPropagation();
                setIsPatchesModalVisible(true);
              }}
            >
              <div className="feature-step-number">3</div>
              {Object.values(selectedPatches).some(patch => patch !== null) && <div className="feature-checkmark">✓</div>}
              <img src="/images/patches.png" alt="Patches" className="feature-icon" />
              <span className="feature-text">Patches</span>
            </button>
          )}
          {/* {!isCoreJacket && (
            <button 
              className="feature-item"
              onClick={(e) => {
                e.stopPropagation();
                setIsPatchesModalVisible(true);
              }}
            >
              <img src="/images/patches.png" alt="Apathc" className="feature-icon" />
              <span className="feature-text">Apathc</span>
            </button>
          )} */}
        </div>
        
        {/* Checkout Button */}
        <div className="checkout-section-bottom">
          <button 
            className="checkout-button"
            onClick={(e) => {
              e.stopPropagation();
              handleCheckout();
            }}
            disabled={isLoading}
          >
            {isLoading && (
              <span className="checkout-loader"></span>
            )}
            <span className="checkout-button-text">
              {isLoading ? 'Adding to Cart...' : 'CHECKOUT'}
            </span>
          </button>
        </div>
        
      </div>

      {/* Jacket Size Modal */}
      {isJacketSizeModalVisible && (
        <div className="modal-overlay" onClick={() => setIsJacketSizeModalVisible(false)}>
          <div className="jacket-size-modal" onClick={(e) => e.stopPropagation()}>
            {/* Drag Handle */}
            <div className="modal-handle" />
            
            {/* Title */}
            <div className="modal-title-container">
              <h3 className="modal-title">JACKET SIZE</h3>
            </div>
            
            {/* Size Chart - Men's or Women's */}
            <div className="size-modal-content">
              <div className="size-chart-title-wrapper">
                <h4 className="size-chart-title-main">
                  {isFemaleCollection ? "Women's Size Chart" : "Men's Size Chart"}
                </h4>
                <p className="size-chart-title-sub">Measurements In Inches</p>
              </div>
              <div className="size-chart-image-wrapper">
                <img 
                  src={isFemaleCollection ? "/images/Women size chart.png" : "/images/Men size chart.png"} 
                  alt={isFemaleCollection ? "Women's Size Chart" : "Men's Size Chart"}
                  className="size-chart-image"
                />
              </div>
              
              {/* {(sizeExtraPrice > 0 || measurementExtraPrice > 0) && (
                <p className="price-adjustment-note-modal-title my-2">THE PRICE IS ADJUSTED AS PER GIVEN SIZE</p>
              )} */}
              
              {/* Size Selection Buttons */}
              <div className="size-buttons-container">
                {availableSizes.map((size) => (
                  <button
                    key={size}
                    className={`size-button ${selectedJacketSize === size ? 'size-button-selected' : ''}`}
                    onClick={() => handleSizeSelection(size)}
                  >
                    <span className={`size-button-text ${selectedJacketSize === size ? 'size-button-text-selected' : ''}`}>
                      {size}
                    </span>
                  </button>
                ))}
              </div>
            </div>
            
            {/* Action Buttons */}
            <div className="jacket-size-modal-actions">
            <button
                className="close-button"
                onClick={() => setIsJacketSizeModalVisible(false)}
              >
                <span className="close-button-text">{selectedJacketSize ? 'Cancel' : 'Close'}</span>
              </button>
              {selectedJacketSize ? (
                <button
                  className="confirm-button"
                  onClick={() => {
                    const measurements = getMeasurementsFromSize(selectedJacketSize);
                    if (measurements) {
                      dispatch(setBodyMeasurements(measurements));
                    }
                    setIsJacketSizeModalVisible(false);
                    setCameFromJacketSizeModal(true);
                    setTimeout(() => {
                      setIsBodySizeModalVisible(true);
                    }, 300);
                  }}
                >
                  <span className="confirm-button-text">Next</span>
                </button>
              ) : null}
           
            </div>
          </div>
        </div>
      )}

      {/* Body Measurements Modal */}
      {isBodySizeModalVisible && (
        <div className="modal-overlay" onClick={() => {
          setIsBodySizeModalVisible(false);
          setCameFromJacketSizeModal(false);
        }}>
          <div className="body-size-modal" onClick={(e) => e.stopPropagation()}>
            {/* Drag Handle */}
            <div className="modal-handle" />
            
            {/* Title on top, then row: [Single image left] [Text right - more space] */}
            <div className="modal-title-container body-measurements-header">
              <h3 className="modal-title">BODY MEASUREMENTS</h3>
              <div className="body-images-with-text-row">
                <div className="body-image-column body-single-image-column">
                  {isFemaleCollection ? (
                    <img src="/images/women body.png" alt="Women body" className="body-measurement-image" />
                  ) : (
                    <img src="/images/Men body.png" alt="Men body" className="body-measurement-image" />
                  )}
                </div>
                <div className="body-center-text-column">
                  <p className="modal-subtitle body-measurements-subtitle">Provide Body Measurements In Inches</p>
                  <div className="measurement-note">
                    <p>Note: Measurements will be adjusted by our tailors for a comfortable, easy-to-wear fit.</p>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Selected Size Info */}
            {selectedJacketSize && SIZE_CHART[selectedJacketSize] && (
              <div className="size-info-display">
                <p className="size-info-text">
                  Selected Size: <strong>{selectedJacketSize}</strong>
                  {getSizeExtraPrice(selectedJacketSize) > 0 && (
                    <span className="size-extra-price"> (+${getSizeExtraPrice(selectedJacketSize)})</span>
                  )}
                </p>
              </div>
            )}
            
            {/* Measurement Warning */}
            {measurementWarning && measurementWarning.exceeds && (
              <div className="measurement-warning">
                <p className="measurement-warning-text">
                  ⚠️ Your measurements correspond to size <strong>{measurementWarning.matchedSize}</strong>, which is larger than selected size <strong>{selectedJacketSize}</strong>.
                  {measurementWarning.extraPrice > 0 && (
                    <span className="measurement-warning-price"> Extra charge: <strong>+${measurementWarning.extraPrice}</strong> will apply.</span>
                  )}
                </p>
              </div>
            )}
            
            {/* Input Fields */}
            <div className="body-size-inputs-container">
              <div className="body-size-input-row">
                <div className="body-size-input-column">
                  <label className="body-size-input-label">Chest (Body)</label>
                  <input
                    className="body-size-input-field"
                    placeholder="add here"
                    type="number"
                    value={tempBodyMeasurements.chest || ''}
                    onChange={(e) => setTempBodyMeasurements({...tempBodyMeasurements, chest: e.target.value})}
                  />
                </div>
                <div className="body-size-input-column">
                  <label className="body-size-input-label">Body Length</label>
                  <input
                    className="body-size-input-field"
                    placeholder="add here"
                    type="number"
                    value={tempBodyMeasurements.bodyLength || ''}
                    onChange={(e) => setTempBodyMeasurements({...tempBodyMeasurements, bodyLength: e.target.value})}
                  />
                </div>
              </div>
              
              <div className="body-size-input-row">
                <div className="body-size-input-column">
                  <label className="body-size-input-label">Sleeve Length</label>
                  <input
                    className="body-size-input-field"
                    placeholder="add here"
                    type="number"
                    value={tempBodyMeasurements.sleeveLength || ''}
                    onChange={(e) => setTempBodyMeasurements({...tempBodyMeasurements, sleeveLength: e.target.value})}
                  />
                </div>
                <div className="body-size-input-column">
                  <label className="body-size-input-label">Shoulder to Shoulder</label>
                  <input
                    className="body-size-input-field"
                    placeholder="add here"
                    type="number"
                    value={tempBodyMeasurements.shoulderToShoulder || ''}
                    onChange={(e) => setTempBodyMeasurements({...tempBodyMeasurements, shoulderToShoulder: e.target.value})}
                  />
                </div>
              </div>
              
              <div className="body-size-input-row">
                <div className="body-size-input-column">
                  <label className="body-size-input-label">{isFemaleCollection ? 'Hip' : 'Arm Width'}</label>
                  <input
                    className="body-size-input-field"
                    placeholder="add here"
                    type="number"
                    value={tempBodyMeasurements.armWidth || ''}
                    onChange={(e) => setTempBodyMeasurements({...tempBodyMeasurements, armWidth: e.target.value})}
                  />
                </div>
                <div className="body-size-input-column">
                  <label className="body-size-input-label">Waist</label>
                  <input
                    className="body-size-input-field"
                    placeholder="add here"
                    type="number"
                    value={tempBodyMeasurements.waist || ''}
                    onChange={(e) => setTempBodyMeasurements({...tempBodyMeasurements, waist: e.target.value})}
                  />
                </div>
              </div>
            </div>
            
            {/* Buttons Row */}
            <div className="modal-buttons-row">
              <button
                className="close-button cancel-button"
                onClick={() => {
                  if (cameFromJacketSizeModal) {
                    setIsBodySizeModalVisible(false);
                    setCameFromJacketSizeModal(false);
                    // Small delay to ensure smooth transition
                    setTimeout(() => {
                      setIsJacketSizeModalVisible(true);
                    }, 300);
                  } else {
                    setIsBodySizeModalVisible(false);
                  }
                }}
              >
                <span className="close-button-text">{cameFromJacketSizeModal ? 'Previous' : 'Cancel'}</span>
              </button>
              <button
                className="apply-button"
                onClick={handleApplyBodySizeChanges}
              >
                <span className="apply-button-text">Apply Changes</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Patches Modal */}
      {isPatchesModalVisible && (
        <div className="modal-overlay" onClick={() => setIsPatchesModalVisible(false)}>
          <div className="patches-modal" onClick={(e) => e.stopPropagation()}>
            {/* Drag Handle */}
            <div className="modal-handle" />
            
            {/* Title */}
            <h3 className="modal-title">{isCoreJacket ? 'PATCHES' : 'APATHC'}</h3>
            <p className="modal-subtitle">Add Your Design</p>
            
            {/* Scrollable Options and Patches */}
            <div className="scrollable-container">
              {['ZIP', 'Right Arm', 'Left Arm', 'Back Side'].map((placement, index) => (
                <div key={placement} className="placement-section">
                  <button
                    className="placement-option"
                    onClick={() => {
                      setSelectedPlacement(placement);
                      setSelectedPatch(0);
                    }}
                  >
                    <span className="placement-option-text">{placement}</span>
                    <div className="placement-number">
                      <span className="placement-number-text">{index + 1}</span>
                    </div>
                  </button>
                  
                  {/* Show patches under selected placement */}
                  {selectedPlacement === placement && (
                    <div className="patch-designs-container">
                      {/* Custom image size controls for Back Side */}
                      {placement === 'Back Side' && selectedPatches['Back Side'] === 'custom' && customPhoto && (
                        <div className="text-controls-container">
                          <div className="text-control-row">
                            <label className="text-control-label">Image Size:</label>
                            <input
                              type="range"
                              min="20"
                              max="150"
                              value={customImageSize}
                              onChange={(e) => setCustomImageSize(parseInt(e.target.value))}
                              className="size-slider"
                            />
                            <span className="size-value">{customImageSize}px</span>
                          </div>
                        </div>
                      )}
                      
                      {/* Text controls for Back Side */}
                      {placement === 'Back Side' && selectedPatches['Back Side'] === 'text' && (
                        <div className="text-controls-container">
                          <div className="text-control-row">
                            <label className="text-control-label">Text:</label>
                            <input
                              type="text"
                              className="text-input-field"
                              placeholder="Enter your text"
                              value={customText}
                              onChange={(e) => setCustomText(e.target.value)}
                            />
                          </div>
                          <div className="text-control-row">
                            <label className="text-control-label">Color:</label>
                            <input
                              type="color"
                              className="color-picker"
                              value={textColor}
                              onChange={(e) => setTextColor(e.target.value)}
                            />
                          </div>
                          <div className="text-control-row">
                            <label className="text-control-label">Size:</label>
                            <input
                              type="range"
                              min="12"
                              max="48"
                              value={textSize}
                              onChange={(e) => setTextSize(e.target.value)}
                              className="size-slider"
                            />
                            <span className="size-value">{textSize}px</span>
                          </div>
                        </div>
                      )}
                      <div className="patch-designs-scroll-content">
                        {getPatchDesignsForPlacement(placement).map((patch, patchIndex) => (
                          <button
                            key={patchIndex}
                            className={`patch-design-button ${(selectedPatch === patchIndex || (placement === 'Back Side' && patch.type === 'text' && selectedPatches['Back Side'] === 'text')) ? 'patch-design-button-selected' : ''}`}
                            onClick={() => {
                              setSelectedPatch(patchIndex);
                              
                              
                              if (patch.type === 'none') {
                                dispatch(setSelectedPatches({
                                  ...selectedPatches,
                                  [placement]: null
                                }));
                                return;
                              }
                              
                              
                              if (patch.type === 'custom') {
                                if (customPhoto) {
                                  removeCustomPhoto();
                                } else {
                                  document.getElementById('custom-photo-input').click();
                                }
                                return;
                              }
                              
                              
                              if (patch.type === 'text') {
                                dispatch(setSelectedPatches({
                                  ...selectedPatches,
                                  'Back Side': 'text'
                                }));
                                return;
                              }
                              
                              
                              const currentSelection = selectedPatches[placement];
                              if (currentSelection === patchIndex) {
                                
                                dispatch(setSelectedPatches({
                                  ...selectedPatches,
                                  [placement]: null
                                }));
                              } else {
                                
                                dispatch(setSelectedPatches({
                                  ...selectedPatches,
                                  [placement]: patchIndex
                                }));
                              }
                            }}
                          >
                            <div className="patch-design-image">
                              {patch.type === 'none' ? (
                                <div className="none-button">
                                  <span className="none-text">None</span>
                                </div>
                              ) : patch.type === 'custom' ? (
                                <div className="custom-upload-button">
                                  <span className="upload-icon">
                                    {customPhoto ? '🗑️' : '📁'}
                                  </span>
                                  <span className="upload-text">
                                    {customPhoto ? 'Remove' : 'Upload'}
                                  </span>
                                  <span className="upload-subtext">
                                    {customPhoto ? 'Custom Image' : 'Vector Image'}
                                  </span>
                                </div>
                              ) : patch.type === 'text' ? (
                                <div className="custom-text-button">
                                  <span className="text-icon">✏️</span>
                                  <span className="text-label">Text</span>
                                </div>
                              ) : (
                                <img 
                                  src={patch.image}
                                  alt={`Patch ${patchIndex + 1}`}
                                  className="patch-design-image-content"
                                />
                              )}
                            </div>
                            {selectedPatch === patchIndex && (
                              <div className="patch-selection-number">
                                <span className="patch-selection-number-text">1</span>
                              </div>
                            )}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
            
            {/* Hidden file input for custom photo */}
            <input
              id="custom-photo-input"
              type="file"
              accept="image/*"
              style={{ display: 'none' }}
              onChange={handleCustomPhotoUpload}
            />
            
            {/* Close Button */}
            <button
              className="close-button"
              onClick={() => setIsPatchesModalVisible(false)}
            >
              <span className="close-button-text">Close</span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductDetailScreen;
